import { Box, Button, useMediaQuery } from '@mui/material';
import React, { useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SafeImage from 'components/SafeImage';
import carouselSizes from './carouselSizes';
import { ProductImage } from '../../generated/graphql';
import { PreviewImage } from './components/PreviewImage';

type Props = {
    images: ProductImage[];
};

export default function Carousel({ images }: Props) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const currentImage = currentImageIndex(images, currentIndex);

    const previewImages = getPreviewImages(images.map((image) => image?.url), currentIndex);
    const isMultipleImages = images.length > 1;
    const imageSize = useCalculateImageSize(isMultipleImages);

    const nextImage = () => {
        if (currentIndex < images.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setCurrentIndex(0);
        }
    };

    const previousImage = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        } else {
            setCurrentIndex(images.length - 1);
        }
    };

    if (images.length === 0) {
        return null;
    }

    return (
        <Box
            className="carousel"
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            >
                <Box>
                    {isMultipleImages ? (
                        <Button onClick={previousImage} sx={{ padding: '100px 0', fontSize: '36px' }}>
                            <ChevronLeftIcon sx={{ transform: 'scale(2.5)' }} />
                        </Button>
                    ) : null}
                </Box>
                <Box sx={{ flexGrow: 1, textAlign: 'center', '& img': { width: '100%', maxWidth: '475px', marginInline: 'auto' } }}>
                    <SafeImage
                        alt="product image"
                        src={images[currentIndex]?.full ?? '/'}
                        blurDataURL={images[currentIndex]?.url ?? '/'}
                        placeholder="blur"
                        height={imageSize}
                        sizes="(max-width: 475px) 100vw, 475px"
                        quality={100}
                        priority
                    />
                </Box>
                <Box>
                    {isMultipleImages ? (
                        <Button onClick={nextImage} sx={{ padding: '100px 0' }}>
                            <ChevronRightIcon sx={{ transform: 'scale(2.5)' }} />
                        </Button>
                    ) : null}
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {previewImages.map((image, index) => (
                    <PreviewImage
                        key={index}
                        currentImage={currentImage}
                        currentIndex={currentIndex}
                        image={image}
                        imageSize={imageSize}
                        index={index}
                        setCurrentIndex={setCurrentIndex}
                    />
                ))}
            </Box>
        </Box>
    );
}

function getPreviewImages(images: string[], currentImage: number) {
    if (images.length < 2) {
        return [];
    }

    const minIndex = Math.max(currentImage - 1, 0);
    const minSlice = minIndex > images.length - 3 ? images.length - 3 : minIndex;
    const maxSlice = minIndex + 3;

    return images.length <= 3 ? images : images.slice(minSlice, maxSlice);
}

function useCalculateImageSize(isMultipleImages: boolean) {
    const addedSize = isMultipleImages ? 0 : 125;
    const isExtraSmall = useMediaQuery('(max-width: 400px)');
    const isSmall = useMediaQuery('(max-width: 500px)');
    const isMedium = useMediaQuery('(max-width: 899px)');

    if (isExtraSmall) {
        return carouselSizes.extraSmall + addedSize;
    }
    if (isSmall) {
        return carouselSizes.small + addedSize;
    }
    if (isMedium) {
        return carouselSizes.medium + addedSize;
    }

    return carouselSizes.large + addedSize;
}

function currentImageIndex(images: ProductImage[], currentIndex: number) {
    if (currentIndex > 1) {
        if (currentIndex >= images.length - 1) {
            return 2;
        }

        return 1;
    }

    return currentIndex;
}
